// components.tsx — small inventory-detail building blocks shared by the
// Batch and Product detail pages. Ported from the prototype's detail views.
import type { CSSProperties, ReactNode } from 'react';
import { useUserName, shortId } from '@/api/useUserNames';
import { Icon } from '@/foundation/icons';
import type { IconName } from '@/foundation/icons';
import { Chip, td, mono } from '@/foundation/primitives';
import { Table, Row } from '@/foundation/common';
import type { Movement, AuditEntry } from '@/domain/types';
import { useEntityAudit } from './api';

export const warnBox: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  padding: '12px 14px',
  borderRadius: 'var(--radius-md)',
  background: 'var(--error-soft)',
  color: 'var(--error-dark)',
  fontSize: 'var(--fs-body)',
  lineHeight: 1.5,
};
export const okBox: CSSProperties = {
  ...warnBox,
  // opaque -lighter, not -soft: success-dark on the -soft blend fails AA on
  // the grey --bg-panel drawer surface (axe, drawer-open audit).
  background: 'var(--success-lighter)',
  color: 'var(--success-dark)',
};
export const infoBox: CSSProperties = {
  ...warnBox,
  background: 'var(--info-soft)',
  color: 'var(--info-dark)',
};

// Alert type → icon, shared by the Alerts page and the dashboard alert feed.
export const ALERT_TYPE_ICON: Record<string, IconName> = {
  Expired: 'ban',
  Expiring: 'clock',
  'Low Stock': 'alerts',
  'Out of Stock': 'xcircle',
  Overstock: 'layers',
  Unused: 'clock',
  'CD Exception': 'lock',
  Blocked: 'lock',
};

const moveTone: Record<string, string> = {
  Inward: 'var(--success-dark)',
  Outward: 'var(--error-dark)',
  Adjustment: 'var(--warning-dark)',
  Transfer: 'var(--info-main)',
};
export function MovementsTable({ moves }: { moves: Movement[] }) {
  const userName = useUserName();
  if (!moves.length)
    return (
      <div className="body2" style={{ color: 'var(--text-secondary)', padding: '8px 0' }}>
        No movements recorded for this selection.
      </div>
    );
  return (
    <div
      style={{
        border: '1px solid var(--divider)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
      }}
    >
      <Table cols={['Movement', 'Type', 'Qty', 'Reference', 'By', 'Date']} rightAlign={[2]}>
        {moves.map((m) => (
          <Row key={m.id}>
            <td style={{ ...td, color: 'var(--text-primary)' }}>
              <span style={mono} title={m.id}>
                {shortId(m.id)}
              </span>
            </td>
            <td style={td}>
              <span style={{ color: moveTone[m.type] || 'var(--text-secondary)', fontWeight: 700 }}>
                {m.type}
              </span>
            </td>
            <td
              style={{
                ...td,
                textAlign: 'right',
                fontWeight: 700,
                color: m.qty < 0 ? 'var(--error-dark)' : 'var(--success-dark)',
              }}
            >
              {m.qty > 0 ? '+' : ''}
              {m.qty}
            </td>
            <td style={td}>
              <span style={mono} title={m.ref}>
                {shortId(m.ref)}
              </span>
            </td>
            <td style={td} title={m.by}>
              {userName(m.by)}
            </td>
            <td style={td}>{m.date}</td>
          </Row>
        ))}
      </Table>
    </div>
  );
}

// Action → icon + colour, so each audit event reads at a glance (not the same grey ⓘ everywhere).
type AuditVisual = { icon: IconName; bg: string; fg: string };
function auditVisual(action: string): AuditVisual {
  const a = action.toLowerCase();
  const violet = { bg: 'var(--primary-lighter)', fg: 'var(--primary-main)' };
  const green = { bg: 'var(--success-soft)', fg: 'var(--success-dark)' };
  const amber = { bg: 'var(--warning-soft)', fg: 'var(--warning-dark)' };
  const red = { bg: 'var(--error-soft)', fg: 'var(--error-dark)' };
  const blue = { bg: 'var(--info-soft)', fg: 'var(--info-main)' };
  if (/remov|delet|dispos|void|reject|cancel/.test(a)) return { icon: 'trash', ...red };
  if (/hold|quarantin|block|recall/.test(a)) return { icon: 'ban', ...amber };
  if (/adjust/.test(a)) return { icon: 'sliders', ...amber };
  if (/edit|updat/.test(a)) return { icon: 'edit', ...violet };
  if (/mov|transfer|reloc/.test(a)) return { icon: 'locations', ...blue };
  if (/approv|verif|post|complet|collect|receiv|confirm|releas/.test(a))
    return { icon: 'checkcircle', ...green };
  if (/creat|add|stag|import|inward/.test(a)) return { icon: 'plus', ...green };
  if (/scan/.test(a)) return { icon: 'barcode', ...blue };
  if (/login|logout|auth|role|permission|grant/.test(a)) return { icon: 'lock', ...blue };
  return { icon: 'info', ...blue };
}
function auditTitle(action: string): string {
  const base = action.includes('.') ? action.split('.').slice(1).join(' ') : action;
  return base.replace(/[._]/g, ' ');
}
const auditPill: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  maxWidth: 260,
  padding: '3px 9px',
  borderRadius: 'var(--radius-pill)',
  background: 'var(--grey-100)',
  fontSize: 'var(--fs-caption)',
  lineHeight: 1.4,
  overflow: 'hidden',
};

/**
 * The audit trail of one record and, optionally, its linked record (e.g. a script and its dispense
 * job). Mount it only where it is shown — it fetches on mount, targeted by reference (A2).
 */
export function EntityAuditTable({
  reference,
  linked,
  compact,
}: {
  reference: string | undefined;
  linked?: string;
  compact?: boolean;
}) {
  const own = useEntityAudit(reference, true);
  const other = useEntityAudit(linked, true);
  if (own.isLoading || other.isLoading)
    return <div style={{ padding: 16, color: 'var(--text-secondary)' }}>Loading audit trail…</div>;
  const rows = [
    ...new Map([...(own.data ?? []), ...(other.data ?? [])].map((a) => [a.id, a])).values(),
  ];
  return <AuditTable rows={rows} compact={compact} />;
}

export function AuditTable({ rows }: { rows: AuditEntry[]; compact?: boolean }) {
  const userName = useUserName();
  if (!rows.length)
    return (
      <div className="body2" style={{ color: 'var(--text-secondary)', padding: '8px 0' }}>
        No audit entries for this selection.
      </div>
    );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {rows.map((a) => {
        const v = auditVisual(a.action);
        const facts = a.facts ?? [];
        const product = facts.find((f) => f.label.toLowerCase() === 'product')?.value;
        const chips = facts.filter((f) => f.label.toLowerCase() !== 'product');
        return (
          <div
            key={a.id}
            style={{
              display: 'flex',
              gap: 14,
              padding: '14px 16px',
              borderRadius: 'var(--radius-lg)',
              background: 'var(--bg-card)',
              border: '1px solid var(--divider)',
              borderLeft: `3px solid ${v.fg}`,
            }}
          >
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 'var(--radius-md)',
                flexShrink: 0,
                background: v.bg,
                color: v.fg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon name={v.icon} size={19} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  flexWrap: 'wrap',
                }}
              >
                <span
                  style={{
                    fontWeight: 700,
                    fontSize: 'var(--fs-emphasis)',
                    color: 'var(--text-primary)',
                    textTransform: 'capitalize',
                  }}
                >
                  {auditTitle(a.action)}
                </span>
                <Chip size="s">{a.entityType || a.entity}</Chip>
                {a.entityId && (
                  <span
                    style={{
                      ...mono,
                      fontSize: 'var(--fs-caption)',
                      color: 'var(--text-disabled)',
                    }}
                  >
                    {a.entityId}
                  </span>
                )}
              </div>
              {product && (
                <div
                  style={{
                    fontSize: 'var(--fs-body)',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    marginTop: 'var(--space-1)',
                  }}
                >
                  {product}
                </div>
              )}
              {chips.length > 0 && (
                <div
                  style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 'var(--space-2)' }}
                >
                  {chips.map((f, i) => (
                    <span key={i} style={auditPill} title={`${f.label}: ${f.value}`}>
                      <span style={{ color: 'var(--text-secondary)' }}>{f.label}</span>
                      <span
                        style={{
                          color: 'var(--text-primary)',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {f.value}
                      </span>
                    </span>
                  ))}
                </div>
              )}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  marginTop: 10,
                  fontSize: 'var(--fs-caption)',
                  color: 'var(--text-disabled)',
                }}
              >
                <Icon name="user" size={12} />
                <span>{userName(a.who)}</span>
                <span>·</span>
                <span>{a.role}</span>
                <span>·</span>
                <span>{a.date}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
